import {lazy as reactLazy} from 'react'
import type {ComponentType, LazyExoticComponent} from 'react'
import {IS_GRAAL} from 'tizra'

/**
 * Start loading a React.lazy() component immediately, on the server only.
 *
 * ReactDOMServer.renderToString(), used by blocks/ssr.tsx on behalf of
 * QuickstartSSR.java, can't suspend: a component which is still loading aborts
 * the render with React error #426. The dynamic imports are inlined into the
 * server bundle (iife format, see vite.config.mts) so they settle as
 * microtasks, which GraalJS drains at the end of the eval, long before any
 * block is rendered.
 *
 * Admin components never render on the server, so they don't need this and can
 * stay out of the server's startup path.
 *
 * Gated on IS_GRAAL rather than !IS_BROWSER so that jsdom tests keep exercising
 * the lazy-loading path that browsers use.
 */
export const preload = (Lazy: LazyExoticComponent<any>) => {
  if (!IS_GRAAL) return
  try {
    // Kick React's lazy initializer, which starts the import and throws the
    // pending thenable. Once it settles, React renders Lazy synchronously.
    const internals = Lazy as unknown as {
      _init: (payload: unknown) => unknown
      _payload: unknown
    }
    internals._init(internals._payload)
  } catch {
    // Expected: the initializer throws the promise it just started.
  }
}

/**
 * React.lazy() which additionally preloads on the server. Use this instead of
 * React.lazy() for anything that can render during SSR.
 */
export const lazy = <T extends ComponentType<any>>(
  load: () => Promise<{default: T}>,
) => {
  const Lazy = reactLazy(load)
  preload(Lazy)
  return Lazy
}
